// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getWarehouse from "@bg/App/Handlers/PostalCodeHandler/getWarehouse";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import TextField from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    newFulfillmentMethod: state.components.addressDrawer.fulfillmentMethod,
    isApplyingChanges: state.components.addressDrawer.isApplyingChanges,
    newPickUpWarehouse: state.components.addressDrawer.pickUpWarehouse,
    postals: (state.xSystemList as any)["AcceptedDeliveryPostalCodes"]?.values,
});

const mapDispatchToProps = {
    getWarehouse,
};

type Props = ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> & {
        fulfillmentMethod: string;
    } & HasToasterContext;

const PostalCode: React.FC<Props> = ({
    getWarehouse,
    toaster,
    fulfillmentMethod,
    newFulfillmentMethod,
    isApplyingChanges,
    newPickUpWarehouse,
    postals,
}) => {
    const [fulfillment, setFulfillment] = React.useState(fulfillmentMethod);
    const [postalCode, setPostalCode] = React.useState(window.localStorage.getItem("postalCode") || "");
    const [error, setError] = React.useState("");
    const [isLoading, setIsloading] = React.useState(false);
    const dispatch = useDispatch();

    // user signed out
    React.useEffect(() => {
        if (window.localStorage.getItem("signedOut") === "true" && postals && postals?.length) {
            if (fulfillment.toLowerCase() === "ship") {
                if (postalCode && postalCode.length > 2 && checkIfNotValid(postalCode)) {
                    window.localStorage.removeItem("signedOut");
                    setPostalCode(postalCode || "");
                    clearStorage();
                } else {
                    setPostalCode(postalCode || "");
                    setError("");
                    getWarehouseData();
                }
            } else {
                setPostalCode(postalCode || "");
                setError("");
                getWarehouseData();
            }
        }
    }, [postals]);

    // if the unauthenticated user changes between ship and pickup, make sure the postalCode is validated against the posta list again
    React.useEffect(() => {
        if (fulfillment.toLowerCase() !== newFulfillmentMethod.toLowerCase() && isApplyingChanges) {
            const postal = window.localStorage.getItem("postalCode");
            const postalId = window.localStorage.getItem("postalCodeID");
            if (newFulfillmentMethod.toLowerCase() !== "pickup" && postalId?.length && postal?.length) {
                // from pickup to ship with invalid postal
                if (postal && postal.length > 2 && checkIfNotValid(postal)) {
                    setPostalCode(postal || "");
                    clearStorage();
                } else {
                    setPostalCode(postal || "");
                    setError("");
                    getWarehouseData();
                }
            }
        }
    }, [newFulfillmentMethod, isApplyingChanges]);

    // on pickup and chooses a location using fulfillment selector case
    React.useEffect(() => {
        const postal = window.localStorage.getItem("postalCode");
        const postalId = window.localStorage.getItem("postalCodeID");
        if (
            newFulfillmentMethod.toLowerCase() === "pickup" &&
            newPickUpWarehouse &&
            newPickUpWarehouse.postalCode !== postal &&
            isApplyingChanges
        ) {
            window.localStorage.setItem("postalCodeID", newPickUpWarehouse.id);
            window.localStorage.setItem("postalCode", newPickUpWarehouse.postalCode);
        } else {
            window.localStorage.setItem("postalCodeID", postalId || "");
            window.localStorage.setItem("postalCode", postal || "");
        }
    }, [isApplyingChanges]);

    const clearStorage = () => {
        window.localStorage.setItem("postalCodeID", "");
        window.localStorage.setItem("postalCode", "");
        setError(`${translate("Unsupported Delivery Location")}`);
        toaster.addToast({
            body: (
                <Typography
                    css={css`
                        margin: 0;
                    `}
                >
                    {translate("Error while setting warehouse.")}
                </Typography>
            ),
            messageType: "warning",
        });
        dispatch({
            type: "commonActionSetter/SetAction",
            payload: {
                name: "resetShipToAddress",
                value: true,
            },
        });
    };

    const checkIfNotValid = (value: string) => {
        if (!postals || !postals.length) {
            return false;
        }
        return !postals.filter((zip: any) => zip.name.toLowerCase().indexOf(value.substring(0, 3).toLowerCase()) === 0)
            .length;
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            event.stopPropagation();
            if (event.currentTarget.value !== "") {
                setError("");
                (event.target as any).blur();
                getWarehouseData(true);
            } else {
                setError(`${translate("Postal code does not accept empty values")}`);
            }
        }
    };

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setError("");
        if (event.currentTarget.value === ".") {
            event.preventDefault();
            return;
        }
        const value = event.target.value;

        // check if at least the first 3 letters exist in any of the supported postals
        if (fulfillment.toLowerCase() === "ship" && value.length > 2 && checkIfNotValid(value)) {
            setPostalCode(value);
            setError(`${translate("Unsupported Delivery Location")}`);
        } else {
            setPostalCode(value);
            setError("");
        }
    };

    const getWarehouseByValue = (postal: string, hasUserChangedPostal?: boolean) => {
        setIsloading(true);
        getWarehouse({
            guestPostalCode: postal,
            hasUserChangedPostal,
            onSuccess: result => {
                setIsloading(false);
                toaster.addToast({
                    body: (
                        <Typography
                            css={css`
                                margin: 0;
                            `}
                        >
                            {translate("Warehouse set to:")}&nbsp;
                            {result}
                        </Typography>
                    ),
                    messageType: "success",
                });
                dispatch({
                    type: "commonActionSetter/SetAction",
                    payload: {
                        name: "resetShipToAddress",
                        value: false,
                    },
                });
            },

            onError: error => {
                setIsloading(false);
                toaster.addToast({
                    body: (
                        <Typography
                            css={css`
                                margin: 0;
                            `}
                        >
                            {translate("Error while setting warehouse.")}&nbsp;{error}
                        </Typography>
                    ),
                    messageType: "warning",
                });
            },
        });
    };

    const getWarehouseData = (userChange?: boolean) => {
        if (!postalCode) {
            setError(`${translate("Postal code does not accept empty values")}`);
        } else if (postalCode.length < 3) {
            setError(`${translate("At least 3 characters required to find a warehouse")}`);
        } else {
            setError("");
            getWarehouseByValue(postalCode, userChange);
        }
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledWrapper
                css={css`
                    padding-bottom: 20px;
                `}
            >
                <Typography as="h4" variant="h5">
                    {siteMessage("Custom_PostalCodeHeader")}
                </Typography>
                <StyledWrapper
                    css={css`
                        margin-bottom: ${fontConfig.spacing.regular};
                    `}
                >
                    <Typography size={17} lineHeight="19px">
                        {siteMessage("Custom_PostalCodeBodyCopy")}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper>
                    <TextField
                        cssOverrides={{
                            formInputWrapper: css`
                                margin-bottom: 15px;
                            `,
                        }}
                        value={postalCode}
                        error={error}
                        type="text"
                        maxLength={7}
                        label={translate("Postal Code")}
                        onKeyPress={handleKeyPress}
                        onChange={handleOnChange}
                    />
                    <Button disabled={!!error} onClick={() => getWarehouseData(true)}>
                        {translate("Set My Postal Code")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default withToaster(connect(mapStateToProps, mapDispatchToProps)(PostalCode));
